// Frontend/geniac/src/Components/common/QuotaExceededModal.tsx
// Modal affiché quand l'utilisateur essaie de créer une ressource mais le quota est dépassé

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FiLock, FiX } from 'react-icons/fi';

interface QuotaExceededModalProps {
  isOpen: boolean;
  onClose: () => void;
  resourceType: string;
  currentUsage: number;
  quotaLimit: number;
  plan: string;
  upgradeSuggestion?: {
    target: string;
    message: string;
    price: string;
  };
}

const QuotaExceededModal: React.FC<QuotaExceededModalProps> = ({
  isOpen,
  onClose,
  resourceType,
  currentUsage,
  quotaLimit,
  plan,
  upgradeSuggestion
}) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const resourceInfo: Record<string, { label: string; singular: string; isMonthly: boolean }> = {
    project: { label: 'projet(s)', singular: 'projet', isMonthly: false },
    architecture: { label: 'architecture(s)', singular: 'architecture', isMonthly: false },
    deployment: { label: 'déploiement(s) par mois', singular: 'déploiement', isMonthly: true },
    api_call: { label: 'appel(s) API par mois', singular: 'appel API', isMonthly: true }
  };

  const info = resourceInfo[resourceType] || { label: resourceType, singular: resourceType, isMonthly: false };

  // Message principal selon spec UX F
  const mainMessage = `Limite atteinte : le plan ${plan.toUpperCase()} autorise ${quotaLimit} ${info.label}.`;

  // Message de solution selon spec UX F
  const getSolutionMessage = (): string => {
    if (info.isMonthly) {
      // Cas spécial pour déploiements/API calls mensuels
      return `Attendez le prochain cycle ou passez à un plan supérieur.`;
    } else {
      // Cas normal pour projets/architectures
      return `Supprimez ${info.singular === 'projet' ? 'un projet' : 'une architecture'} ou passez à un plan supérieur.`;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg p-6 max-w-md shadow-2xl">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            <FiLock className="text-orange-600" size={32} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Limite atteinte</h2>
              <p className="text-sm text-gray-500">Plan {plan.toUpperCase()}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Message principal - Spec UX F */}
        <p className="text-gray-700 mb-2">
          {mainMessage}
        </p>

        {/* Message de solution - Spec UX F */}
        <p className="text-gray-600 mb-4">
          {getSolutionMessage()}
        </p>

        {/* Utilisation actuelle (informationnel) */}
        <div className="bg-gray-100 p-3 rounded mb-4">
          <p className="text-sm text-gray-600">
            Utilisation actuelle : <strong>{currentUsage}/{quotaLimit}</strong>
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={() => {
              onClose();
              navigate('/settings/subscription');
            }}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Voir les plans
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuotaExceededModal;
